/**
 * ErrorStates — dedicated error, warning, and empty-state components
 * matching the error-states mockup. Each component uses the Wayne Forest
 * palette, lucide-react icons, and the AppleButton atom for primary actions.
 *
 * Exports:
 *   NetworkOffline, RateLimited, AgentError, WorkflowFailed,
 *   ConnectionFailed, GuardrailBlocked, PartialFailure, NotFoundPage,
 *   SessionExpired, EmptyState
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  CloudOff,
  Lock,
  Clock,
  Unplug,
  Shield,
  type LucideIcon,
} from 'lucide-react';
import { AppleButton } from '@/components/atoms/AppleButton';

/* ─────────────────────────────────────────────────────────────────────────────
 * Shared layout wrappers
 * ───────────────────────────────────────────────────────────────────────────── */

function FullPageLayout({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center text-center min-h-[280px] px-8 py-12">
      {children}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
 * 1. NetworkOffline
 * ───────────────────────────────────────────────────────────────────────────── */

interface NetworkOfflineProps {
  onRetry: () => void;
  message?: string;
}

export function NetworkOffline({
  onRetry,
  message = "Wayne can’t reach the server. Check your connection and try again.",
}: NetworkOfflineProps): JSX.Element {
  return (
    <FullPageLayout>
      <div
        role="alert"
        aria-live="assertive"
        className="flex flex-col items-center"
      >
        <CloudOff
          size={56}
          strokeWidth={1.5}
          className="text-wayne-ink-3 opacity-70 mb-5"
          aria-hidden="true"
        />
        <h2 className="font-serif text-[22px] font-medium text-wayne-ink mb-2">
          You&apos;re offline
        </h2>
        <p className="text-wayne-ink-2 text-sm max-w-[360px] mb-6 leading-relaxed">
          {message}
        </p>
        <AppleButton onClick={onRetry}>Retry</AppleButton>
      </div>
    </FullPageLayout>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
 * 2. RateLimited
 * ───────────────────────────────────────────────────────────────────────────── */

interface RateLimitedProps {
  retryAfterSeconds: number;
  message?: string;
  onRetry?: () => void;
}

export function RateLimited({
  retryAfterSeconds,
  message = "You've made too many requests. Try again shortly.",
  onRetry,
}: RateLimitedProps): JSX.Element {
  const [remaining, setRemaining] = useState(retryAfterSeconds);

  useEffect(() => {
    setRemaining(retryAfterSeconds);
  }, [retryAfterSeconds]);

  useEffect(() => {
    if (remaining <= 0) return;
    const timer = window.setInterval(() => {
      setRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [remaining]);

  return (
    <div
      role="status"
      aria-live="polite"
      className="border-l-4 border-l-wayne-orange border border-wayne-hairline rounded-r-[10px] bg-wayne-surface px-5 py-4"
    >
      <p className="font-semibold text-sm text-wayne-orange mb-1">
        Slow down
      </p>
      <p className="text-wayne-ink-2 text-[13px] leading-relaxed">{message}</p>
      <div className="inline-flex items-center gap-1.5 mt-2.5 text-xs font-medium text-wayne-orange font-mono">
        <Clock size={14} aria-hidden="true" />
        {remaining > 0 ? (
          <span>Retry in {remaining}s...</span>
        ) : (
          <span>Ready to retry</span>
        )}
      </div>
      {remaining === 0 && onRetry && (
        <div className="mt-3">
          <AppleButton onClick={onRetry}>Retry now</AppleButton>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
 * 3. AgentError
 * ───────────────────────────────────────────────────────────────────────────── */

interface AgentErrorProps {
  agentName: string;
  errorSummary: string;
  onRetry: () => void;
  onDismiss?: () => void;
}

export function AgentError({
  agentName,
  errorSummary,
  onRetry,
  onDismiss,
}: AgentErrorProps): JSX.Element {
  return (
    <div
      role="alert"
      aria-live="assertive"
      className="border-[1.5px] border-wayne-red rounded-xl px-5 py-4 max-w-[520px] bg-wayne-surface"
    >
      <div className="flex items-center gap-2 mb-2 text-xs text-wayne-ink-3">
        <span
          className="w-2 h-2 rounded-full bg-wayne-ink-3 shrink-0"
          aria-hidden="true"
        />
        <span>{agentName}</span>
      </div>
      <p className="text-sm text-wayne-ink-2 leading-relaxed mb-3.5">
        {errorSummary}
      </p>
      <div className="flex items-center gap-3">
        <AppleButton onClick={onRetry}>Retry</AppleButton>
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className="text-[13px] text-wayne-accent hover:underline cursor-pointer"
          >
            Dismiss
          </button>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
 * 4. WorkflowFailed
 * ───────────────────────────────────────────────────────────────────────────── */

interface WorkflowFailedProps {
  workflowName: string;
  stepName: string;
  error: string;
  executionUrl?: string;
  onRetry?: () => void;
}

export function WorkflowFailed({
  workflowName,
  stepName,
  error,
  executionUrl,
  onRetry,
}: WorkflowFailedProps): JSX.Element {
  return (
    <div
      role="alert"
      aria-live="assertive"
      className="rounded-[10px] px-5 py-4 border border-wayne-hairline bg-wayne-surface"
    >
      <div className="flex items-center gap-2.5 mb-2">
        <span
          className="w-2 h-2 rounded-full bg-wayne-red shrink-0"
          aria-hidden="true"
        />
        <span className="font-semibold text-sm text-wayne-ink">
          {workflowName}
        </span>
      </div>
      <p className="text-[13px] text-wayne-ink-2 leading-relaxed mb-3.5 pl-[18px]">
        {stepName} failed: {error}
      </p>
      <div className="flex items-center gap-3 pl-[18px]">
        {onRetry && (
          <AppleButton onClick={onRetry}>Retry step</AppleButton>
        )}
        {executionUrl && (
          <Link
            to={executionUrl}
            className="text-[13px] text-wayne-accent hover:underline"
          >
            View execution
          </Link>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
 * 5. ConnectionFailed
 * ───────────────────────────────────────────────────────────────────────────── */

interface ConnectionFailedProps {
  sourceName: string;
  reason?: string;
  onReconnect: () => void;
}

export function ConnectionFailed({
  sourceName,
  reason = 'Connection lost. Re-authenticate to continue.',
  onReconnect,
}: ConnectionFailedProps): JSX.Element {
  return (
    <div
      role="status"
      aria-live="polite"
      className="rounded-[10px] px-5 py-4 border border-wayne-hairline bg-wayne-surface"
    >
      <div className="flex items-center gap-2.5 mb-2">
        <span
          className="w-2 h-2 rounded-full bg-wayne-orange shrink-0"
          aria-hidden="true"
        />
        <span className="font-semibold text-sm text-wayne-ink">
          {sourceName} — Connection failed
        </span>
      </div>
      <p className="text-[13px] text-wayne-ink-2 leading-relaxed mb-3.5 pl-[18px]">
        {reason}
      </p>
      <div className="pl-[18px]">
        <AppleButton onClick={onReconnect}>
          <Unplug size={14} aria-hidden="true" />
          Reconnect
        </AppleButton>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
 * 6. GuardrailBlocked
 * ───────────────────────────────────────────────────────────────────────────── */

interface GuardrailBlockedProps {
  message?: string;
}

export function GuardrailBlocked({
  message = 'This response was blocked by content safety guardrails. The query may contain sensitive information. Please rephrase your question.',
}: GuardrailBlockedProps): JSX.Element {
  return (
    <div
      role="status"
      aria-live="polite"
      className="border-[1.5px] border-wayne-orange rounded-xl px-5 py-4 max-w-[520px] bg-wayne-surface"
    >
      <div className="flex items-center gap-2 mb-2 text-xs text-wayne-ink-3">
        <Shield
          size={14}
          className="text-wayne-orange"
          aria-hidden="true"
        />
        <span>Content Safety</span>
      </div>
      <p className="text-sm text-wayne-ink-2 leading-relaxed">{message}</p>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
 * 7. PartialFailure
 * ───────────────────────────────────────────────────────────────────────────── */

type AgentStatus = 'success' | 'error' | 'timeout';

interface AgentResult {
  name: string;
  status: AgentStatus;
}

interface PartialFailureProps {
  agents: AgentResult[];
  onRetryFailed: () => void;
}

const STATUS_DOT_COLOR: Record<AgentStatus, string> = {
  success: 'bg-wayne-green',
  error: 'bg-wayne-red',
  timeout: 'bg-wayne-orange',
};

const STATUS_TEXT_COLOR: Record<AgentStatus, string> = {
  success: 'text-wayne-green',
  error: 'text-wayne-red',
  timeout: 'text-wayne-orange',
};

const STATUS_LABEL: Record<AgentStatus, string> = {
  success: 'Completed',
  error: 'Error',
  timeout: 'Timed out',
};

export function PartialFailure({
  agents,
  onRetryFailed,
}: PartialFailureProps): JSX.Element {
  const successCount = agents.filter((a) => a.status === 'success').length;
  const totalCount = agents.length;

  return (
    <div
      role="status"
      aria-live="polite"
      className="p-5 border border-wayne-hairline rounded-xl bg-wayne-surface"
    >
      <p className="text-sm text-wayne-ink-2 mb-4 leading-relaxed">
        {successCount} of {totalCount} agents responded successfully.
      </p>
      <div className="flex flex-col gap-2 mb-4">
        {agents.map((agent) => (
          <div
            key={agent.name}
            className="flex items-center gap-2.5 text-[13px] text-wayne-ink-2"
          >
            <span
              className={`w-2 h-2 rounded-full shrink-0 ${STATUS_DOT_COLOR[agent.status]}`}
              aria-hidden="true"
            />
            <span className="font-medium min-w-[100px]">{agent.name}</span>
            <span className={`${STATUS_TEXT_COLOR[agent.status]}`}>
              {STATUS_LABEL[agent.status]}
            </span>
          </div>
        ))}
      </div>
      <AppleButton onClick={onRetryFailed}>Retry failed agents</AppleButton>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
 * 8. NotFoundPage
 * ───────────────────────────────────────────────────────────────────────────── */

export function NotFoundPage(): JSX.Element {
  return (
    <FullPageLayout>
      <div
        role="alert"
        aria-live="polite"
        className="flex flex-col items-center"
      >
        <h1 className="text-[96px] font-semibold text-wayne-ink-3 opacity-30 leading-none mb-2">
          404
        </h1>
        <p className="text-wayne-ink-2 text-[15px] mb-6">
          This page doesn&apos;t exist.
        </p>
        <Link to="/">
          <AppleButton>Go home</AppleButton>
        </Link>
      </div>
    </FullPageLayout>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
 * 9. SessionExpired
 * ───────────────────────────────────────────────────────────────────────────── */

interface SessionExpiredProps {
  onLogin: () => void;
  message?: string;
}

export function SessionExpired({
  onLogin,
  message = 'Your Midway session has ended. Sign in again to continue.',
}: SessionExpiredProps): JSX.Element {
  return (
    <FullPageLayout>
      <div
        role="alert"
        aria-live="assertive"
        className="flex flex-col items-center"
      >
        <Lock
          size={56}
          strokeWidth={1.5}
          className="text-wayne-ink-3 opacity-70 mb-5"
          aria-hidden="true"
        />
        <h2 className="font-serif text-[22px] font-medium text-wayne-ink mb-2">
          Session expired
        </h2>
        <p className="text-wayne-ink-2 text-sm max-w-[360px] mb-6 leading-relaxed">
          {message}
        </p>
        <AppleButton onClick={onLogin}>Sign in</AppleButton>
      </div>
    </FullPageLayout>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
 * 10. EmptyState
 * ───────────────────────────────────────────────────────────────────────────── */

interface EmptyStateProps {
  icon?: LucideIcon;
  title?: string;
  description: string;
  actionLabel?: string;
  onAction?: () => void;
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  actionLabel,
  onAction,
}: EmptyStateProps): JSX.Element {
  return (
    <div className="flex flex-col items-center text-center px-6 py-8">
      {Icon && (
        <Icon
          size={40}
          strokeWidth={1.5}
          className="text-wayne-ink-3 opacity-50 mb-3.5"
          aria-hidden="true"
        />
      )}
      {title && (
        <h3 className="font-serif text-base font-medium text-wayne-ink mb-1">
          {title}
        </h3>
      )}
      <p className="text-sm text-wayne-ink-3 max-w-[300px] leading-relaxed">
        {description}
      </p>
      {actionLabel && onAction && (
        <div className="mt-4">
          <AppleButton onClick={onAction}>{actionLabel}</AppleButton>
        </div>
      )}
    </div>
  );
}

